import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PageLoading } from '@/components/ui/loading';
import { refreshBadge, dismissShadeNotification, dismissAllShadeNotifications } from '@/lib/push-client';
import { Bell, FileText, Activity, CheckCheck, Trash2, Send, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface NotificationItem {
  id: string;
  title: string;
  message: string;
  type: string;
  entity_type: string;
  entity_id: string;
  is_read: number | boolean;
  created_at: string;
}

type Filter = 'all' | 'unread' | 'read';

const TABS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'read', label: 'Read' },
];

/** Matches the Worker's deep-link resolution so a tap opens the existing record. */
function notificationHref(n: NotificationItem): string {
  if (n.entity_type === 'requirement' && n.entity_id) return `/requirements/${n.entity_id}`;
  if (n.entity_type === 'actual_status' && n.entity_id) return `/actuals/${n.entity_id}`;
  return '/notifications';
}

function notificationIcon(n: NotificationItem) {
  if (n.entity_type === 'requirement') return <FileText size={18} className="text-brand-500" />;
  if (n.entity_type === 'actual_status') return <Activity size={18} className="text-brand-500" />;
  return <Bell size={18} className={n.is_read ? 'text-muted' : 'text-brand-500'} />;
}

/** Compact timestamp that always stays visible: today -> time, else date. */
function formatCompactTime(date: string): string {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
  if (sameDay) {
    return new Intl.DateTimeFormat('en-IN', { hour: '2-digit', minute: '2-digit' }).format(d);
  }
  return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short' }).format(d);
}

function createSafeTitle(date: string): string {
  const d = new Date(date);
  return isNaN(d.getTime()) ? '' : new Intl.DateTimeFormat('en-IN', {
    day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(d);
}

function countFor(list: NotificationItem[], filter: Exclude<Filter, 'all'>): number {
  return list.filter(n => (filter === 'unread' ? !n.is_read : !!n.is_read)).length;
}

// TEMPORARY TEST-ONLY FEATURE.
// Shows a "Send Test Push" button so staging/development can verify Web Push
// delivery before launch. Enabled only when the test feature flag is set. The
// backend's GET /notifications/push/test endpoint independently returns 403
// when ENVIRONMENT === "production", so this can never actually send in
// production.
const TEST_PUSH_ENABLED = import.meta.env.VITE_ENABLE_TEST_USER_SWITCH === 'true';

interface TestPushResponse {
  sent?: boolean;
  sentCount?: number;
  status?: string;
  reason?: string;
  message?: string;
  error?: string;
  activeSubscriptionCount?: number;
  outcomes?: { ok: boolean; endpointHost?: string; statusCode?: number | null }[];
  data?: {
    sent?: boolean;
    sentCount?: number;
    status?: string;
    reason?: string;
    message?: string;
    outcomes?: { ok: boolean; endpointHost?: string; statusCode?: number | null }[];
  };
}

export default function NotificationsPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>('all');
  const [testPending, setTestPending] = useState(false);
  const [testStatus, setTestStatus] = useState<{ text: string; tone: 'success' | 'error' | 'warning' } | null>(null);

  async function sendTestPush() {
    if (testPending) return;
    setTestPending(true);
    setTestStatus(null);
    try {
      const res = await api.get<TestPushResponse>('/notifications/push/test');
      // The backend returns a flat contract ({ sent, status, message, reason, ... })
      let sent = res.sent;
      let sentCount = res.sentCount;
      let status = res.status;
      let reason = res.reason;
      let message = res.message;
      // Defensive: if the response was nested under { data: {...} }, read from there.
      if ((sent === undefined || status === undefined) && res.data) {
        sent = res.data.sent ?? sent;
        sentCount = res.data.sentCount ?? sentCount;
        status = res.data.status ?? status;
        reason = res.data.reason ?? reason;
        message = res.data.message ?? message;
      }
      if (sent === true && (sentCount ?? 0) > 0) {
        setTestStatus({ text: message || 'Push sent successfully. Check your phone.', tone: 'success' });
      } else {
        const detail = message || reason || status || 'No push was sent.';
        setTestStatus({ text: `Test push not sent. ${detail}`, tone: 'warning' });
      }
    } catch (err: any) {
      setTestStatus({ text: err?.message || 'Test push failed.', tone: 'error' });
    } finally {
      setTestPending(false);
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const r = await api.get<{ data: NotificationItem[] }>('/notifications');
      return r.data ?? [];
    },
  });

  const readAllM = useMutation({
    mutationFn: () => api.put('/notifications/read-all', {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      void refreshBadge();
      dismissAllShadeNotifications();
    },
  });
  const readM = useMutation({
    mutationFn: (id: string) => api.put(`/notifications/${id}/read`, {}),
    onSuccess: (_, id) => {
      const n = data?.find((x) => x.id === id);
      qc.invalidateQueries({ queryKey: ['notifications'] });
      void refreshBadge();
      if (n) dismissShadeNotification(n.entity_type, n.entity_id);
    },
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/notifications/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      void refreshBadge();
    },
  });

  if (isLoading) return <PageLoading />;

  const list = data ?? [];
  const filtered = filter === 'all'
    ? list
    : list.filter(n => (filter === 'unread' ? !n.is_read : !!n.is_read));

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-primary">Notifications</h1>
          <p className="text-sm text-secondary mt-0.5">Alerts for labour approvals and submissions</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="min-h-[44px] self-start sm:self-auto text-error border-error"
            onClick={sendTestPush}
            disabled={testPending}
          >
            {testPending ? (
              <Loader2 size={16} className="mr-1 animate-spin" />
            ) : (
              <Send size={16} className="mr-1" />
            )}
            Send Test Push
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="min-h-[44px] self-start sm:self-auto"
            onClick={() => readAllM.mutate()}
            disabled={readAllM.isPending}
          >
            <CheckCheck size={16} className="mr-1" />
            Mark All Read
          </Button>
        </div>
      </div>

      {TEST_PUSH_ENABLED && testStatus && (
        <p
          className={`text-sm font-medium ${
            testStatus.tone === 'success'
              ? 'text-success'
              : testStatus.tone === 'error'
                ? 'text-error'
                : 'text-amber-600 dark:text-amber-400'
          }`}
          role="status"
        >
          {testStatus.text}
        </p>
      )}

      <div role="tablist" aria-label="Filter notifications" className="grid w-full grid-cols-3 rounded-lg bg-surface-secondary p-1 text-secondary">
        {TABS.map(tab => {
          const active = filter === tab.key;
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={active}
              aria-label={`${tab.label} notifications`}
              onClick={() => setFilter(tab.key)}
              className={cn(
                'inline-flex min-h-[44px] items-center justify-center rounded-md px-3 text-sm font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
                active ? 'bg-surface text-brand-500 shadow-sm' : 'text-secondary hover:bg-surface-hover'
              )}
            >
              {tab.label}
              {tab.key !== 'all' && (
                <span className="ml-1 text-xs opacity-70" aria-hidden="true">
                  ({countFor(list, tab.key)})
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="space-y-2 sm:space-y-3">
        {filtered.length === 0 ? (
          <p className="py-12 text-center text-muted">
            No {filter === 'all' ? '' : filter + ' '}notifications
          </p>
        ) : (
          filtered.map(n => {
            const href = notificationHref(n);
            const unread = !n.is_read;
            return (
              <Card
                key={n.id}
                className={cn('overflow-hidden', unread && 'border-l-4 border-l-brand-500')}
              >
                <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-secondary text-brand-500">
                    {notificationIcon(n)}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <Link
                        to={href}
                        className="min-w-0 flex-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                        aria-label={`${n.title}${unread ? '. Unread.' : '.'} ${n.message}`}
                      >
                        <p className="truncate text-sm font-medium text-primary">{n.title}</p>
                        <p className="mt-0.5 line-clamp-2 text-sm text-secondary break-words">{n.message}</p>
                      </Link>
                      <time
                        dateTime={n.created_at}
                        className="shrink-0 text-xs text-muted whitespace-nowrap"
                        title={createSafeTitle(n.created_at)}
                      >
                        {formatCompactTime(n.created_at)}
                      </time>
                    </div>

                    <div className="mt-2 flex items-center justify-between gap-2 border-t border-default pt-2">
                      <div className="flex min-w-0 items-center">
                        {unread && (
                          <span
                            role="status"
                            aria-label="Unread"
                            className="inline-flex items-center gap-1 rounded-full bg-brand-500 px-2 py-0.5 text-xs font-medium text-white"
                          >
                            Unread
                          </span>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {unread && (
                          <Button
                            variant="ghost"
                            className="h-11 min-w-[44px] px-3"
                            onClick={() => readM.mutate(n.id)}
                            title="Mark as read"
                            aria-label="Mark as read"
                            disabled={readM.isPending}
                          >
                            <CheckCheck size={18} />
                            <span className="hidden sm:inline">Read</span>
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          className="h-11 min-w-[44px] px-3 text-error"
                          onClick={() => deleteM.mutate(n.id)}
                          title="Delete"
                          aria-label="Delete notification"
                          disabled={deleteM.isPending}
                        >
                          <Trash2 size={18} />
                          <span className="hidden sm:inline">Delete</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
